import {
  DRAFT_MAX_AGE_MS,
  offlineStorage,
  type InspectionDraft,
} from '../../services/offlineStorage';

/**
 * The answers to a finished inspection stay on the phone until its report is
 * sent, so the inspector can come back to it from the site screen and edit.
 *
 * Jim, Teams 2026-10-05, on the site screen's inspection row: "add the clock
 * showing pending send and the edit button here." Until then Edit only
 * existed on the review screen, where the answers were still in memory; once
 * the inspector left, a finished report could be stopped but not changed.
 */

/** Work the inspector did: an answer, or anything in the City of Austin section. */
const hasAnswers = ({ payload }: InspectionDraft) =>
  Object.keys(payload.responses || {}).length > 0 ||
  (payload.austinTaps?.length ?? 0) > 0 ||
  Object.keys(payload.austinConditions || {}).length > 0 ||
  Boolean(payload.austinNotes?.trim());

const isStale = (draft: InspectionDraft, now: number) =>
  now - draft.savedAt > DRAFT_MAX_AGE_MS;

/** Answered on this device and never completed: what "Resume?" is about. */
export function isUnfinished(draft: InspectionDraft, now = Date.now()) {
  return hasAnswers(draft) && !isStale(draft, now) && !draft.payload.submitted;
}

export type DraftPlan =
  /** Nothing to pick up: start a new inspection. */
  | { kind: 'fresh' }
  /** Too old to trust: clear it and start new. */
  | { kind: 'stale' }
  /** An unfinished inspection: ask whether to resume it. */
  | { kind: 'prompt'; draft: InspectionDraft }
  /** Edit: put the finished inspection's answers back. */
  | { kind: 'reopen'; draft: InspectionDraft }
  /** Edit was asked for, but those answers are not on this device. */
  | { kind: 'unavailable' };

/** What the inspection flow does with the draft it finds when it opens. */
export function draftPlan(
  draft: InspectionDraft | null,
  editId: string | null,
  now = Date.now()
): DraftPlan {
  if (editId) {
    return draft?.payload.submitted?.inspectionId === editId
      ? { kind: 'reopen', draft }
      : { kind: 'unavailable' };
  }
  if (!draft || !hasAnswers(draft)) return { kind: 'fresh' };
  if (isStale(draft, now)) return { kind: 'stale' };
  // Filed already: not something to "resume". It stays for Edit until the
  // report goes, or until a new inspection's answers replace it.
  if (draft.payload.submitted) return { kind: 'fresh' };
  return { kind: 'prompt', draft };
}

/** The finished inspection this device can reopen for a site, if any. */
export async function editableInspectionId(
  siteId: string,
  userId: string
): Promise<string | null> {
  await offlineStorage.init();
  const draft = await offlineStorage.getDraft(siteId, userId);
  return draft?.payload.submitted?.inspectionId ?? null;
}

/**
 * Once the report has gone the answers have done their job. Returns true
 * when a kept draft was cleared.
 */
export async function forgetIfSent(
  siteId: string,
  userId: string,
  sentInspectionIds: string[]
): Promise<boolean> {
  const kept = await editableInspectionId(siteId, userId);
  if (!kept || !sentInspectionIds.includes(kept)) return false;
  await offlineStorage.clearDraft(siteId, userId);
  return true;
}
